import React, { useState } from 'react'
import { popup } from '@/popup_handler'
import { HistoryService } from '@/api'
import { useQuery } from '@tanstack/react-query'
import SpinnerLoader from '@/ui/spinner_loader'
import { OrderStatus } from '@/interface'

interface User {
  _id: string
  name: string
  username: string
  address?: string
}

interface ConversionRate {
  AED: number
  USD: number
  EUR: number
  INR: number
}

interface OrderDataAction {
  gasLimit: number
  payload: string
}

interface OrderDataInput {
  chainId: number
  token: string
  amount: string
}

interface OrderDataOutput {
  token: string
  minAmountOut: string
  recipient: string
  chainId: number
}

interface ParsedOrderData {
  inputs: OrderDataInput[]
  output: OrderDataOutput
  sender: string
  nonce: string
  initiateDeadline: number
  fillDeadline: number
  settlementExpiry: number
  metadata: string
  action: OrderDataAction
  crayInputOrderType: number
}

interface Allowance {
  domainData: {
    name: string
    version: string
    chainId: number
    verifyingContract: string
  }
  types: any
  values: any
}

interface Transaction {
  _id: string
  senderUser: User
  receiverUser: User
  senderAddress: string
  receiverAddress: string
  destinationChain: number
  amount: string
  minAmountOut: string

  isSponsered: boolean

  /** Optional fields present in some records */
  orderData?: string | ParsedOrderData
  remark?: string
  destinationToken?: string

  status: string
  stage: string
  orderType: string
  orderHash: string

  destinationPayload: string
  destinationGasLimit: number

  allowance: Allowance[]
  testnet: boolean

  conversionRate: ConversionRate

  signedOrder: any[]
  settlementBreakdown: any[]

  createdAt: string
  updatedAt: string
  __v: number
  isSelf: boolean
  direction: number

  txUser: User
}

// Example transactionData (keep your data here)

// --- Components ---
const TransactionItem: React.FC<{
  transaction: Transaction
  onClick: (tx: Transaction) => void
}> = ({ transaction, onClick }) => {
  const senderName = transaction.senderUser?.name || 'Unknown'

  const formatDateTime = (isoString: string) => {
    const date = new Date(isoString)
    return `${date.toLocaleDateString()} at ${date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    })}`
  }

  const shortAmount = Number(transaction.amount).toFixed(6)
  const usd = (parseFloat(transaction.amount) * transaction.conversionRate.USD).toFixed(2)

  return (
    <div onClick={() => onClick(transaction)} className="flex items-center justify-between py-3 cursor-pointer hover:bg-gray-100 px-2 rounded">
      <div className="flex items-center space-x-3">
        <div className="w-10 h-10 flex items-center justify-center rounded-full bg-red-100 text-red-600 font-bold text-lg">{senderName[0]}</div>
        <div>
          <div className="text-base font-semibold text-gray-800">{senderName}</div>
          <div className="text-xs text-gray-500">{formatDateTime(transaction.createdAt)}</div>
        </div>
      </div>
      <div className="text-right">
        <div className={`text-base font-semibold ${transaction.direction ? 'text-green-500' : 'text-foreground'}`}>
          {transaction.direction ? '+' : '-'} {shortAmount} USDC
        </div>
        <div className="text-xs text-gray-500">≈ ${usd}</div>
      </div>
    </div>
  )
}

const TransactionHistory: React.FC = () => {
  const [activeFilter, setActiveFilter] = useState<'all' | 'today' | 'week' | 'month'>('all')
  const transactions = useQuery(['transactions'], () => HistoryService.Get())

  const filterTransactions = () => {
    const now = new Date()
    return (
      transactions.data?.filter((tx: any) => {
        if (tx.status !== OrderStatus.COMPLETED) return
        const txDate = new Date(tx.createdAt)
        if (activeFilter === 'today') return txDate.getDate() === now.getDate() && txDate.getMonth() === now.getMonth() && txDate.getFullYear() === now.getFullYear()
        if (activeFilter === 'week') return now.getTime() - txDate.getTime() <= 7 * 24 * 60 * 60 * 1000
        if (activeFilter === 'month') return now.getTime() - txDate.getTime() <= 30 * 24 * 60 * 60 * 1000
        return true
      }) || []
    )
  }

  const filteredList = filterTransactions() as any
  console.log(9, filteredList)
  // Calculate total collection
  const totalCollection = filteredList
    .filter((tx: any) => tx.direction)
    .reduce((acc: any, tx: any) => acc + parseFloat(tx.amount), 0)
    .toFixed(4)

  const handleNotification = (notification: string | null) => {
    if (!notification) return
    const data = notification
    popup.open('payment_receipt', {
      _id: data,
    })
  }
  if (transactions.isLoading) return <SpinnerLoader />
  return (
    <div className="w-full max-w-sm bg-white rounded-3xl  overflow-y-scroll no-scrollbar p-4">
      {/* Filter Tabs */}
      <div className="flex items-center justify-center">
        <div className="flex space-x-4 bg-gray-100 rounded-xl p-1 mb-4">
          {['all', 'today', 'week', 'month'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveFilter(tab as any)}
              className={`px-4 py-1.5 rounded-xl text-sm font-semibold ${activeFilter === tab ? 'bg-white shadow-md' : 'hover:bg-gray-200'}`}
            >
              {tab === 'week' ? '1W' : tab === 'month' ? '1M' : tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Summary */}
      <div className="text-center py-6">
        <div className="text-sm font-medium text-gray-500">Total collection</div>
        <div className="text-3xl font-bold text-gray-900 my-1">{totalCollection} USDC</div>
        <div className="text-sm text-gray-500">{filteredList.length} transactions</div>
      </div>

      {/* Recent Transactions */}
      <div className="space-y-1 mb-10">
        {filteredList.map((tx: any) => (
          <TransactionItem key={tx._id} transaction={tx} onClick={() => handleNotification(tx._id)} />
        ))}
      </div>
    </div>
  )
}

export default TransactionHistory
